/**
 * A component that displays markdown content formatted with github-markdown-css
 */

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import 'github-markdown-css/github-markdown.css';
import markdownStyle from './github-markdown.module.css';
import { ChatMarkdownRendererProps } from './ChatMarkdownRenderer.types';
import { useEffect, useState, memo, useMemo } from 'react';
import { useAnimatedText } from '../../model/useAnimatedText';
import { withoutCitationMarkers } from '../../lib/citationMarkers';

export const ChatMarkdownRenderer = memo(
  ({
    content,
    disableAnimation = false,
    isStreaming = false,
    inline = false,
  }: ChatMarkdownRendererProps) => {
    const [visible, setVisible] = useState(disableAnimation); // Start visible if animations disabled
    const [hasAnimated, setHasAnimated] = useState(false);
    const animatedText = useAnimatedText({
      text: withoutCitationMarkers(content),
      enabled: isStreaming,
    });

    useEffect(() => {
      if (disableAnimation) {
        setVisible(true);
        return;
      }

      // Only animate once per component mount, not on every content change
      if (!hasAnimated) {
        setVisible(false);
        const timer = setTimeout(() => {
          setVisible(true);
          setHasAnimated(true);
        }, 20);
        return () => clearTimeout(timer);
      }
    }, [disableAnimation, hasAnimated]);

    const fadeStyle = useMemo<React.CSSProperties>(
      () => ({
        opacity: visible ? 1 : 0,
        transition: disableAnimation ? 'none' : 'opacity 0.2s', // Disable transition for widget messages
      }),
      [visible, disableAnimation],
    );

    const components = useMemo(
      () => ({
        a: (props: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
          <a {...props} target="_blank" rel="noopener noreferrer">
            {props.children}
          </a>
        ),
        ol: ({ children, ...props }: React.OlHTMLAttributes<HTMLOListElement>) => (
          <ol {...props} style={{ listStyleType: 'decimal' }}>
            {children}
          </ol>
        ),
        ul: ({ children, ...props }: React.HTMLAttributes<HTMLUListElement>) => (
          <ul {...props} style={{ listStyleType: 'disc' }}>
            {children}
          </ul>
        ),
        ...(inline && {
          p: ({ children }: { children?: React.ReactNode }) => <span>{children}</span>,
        }),
      }),
      [inline],
    );

    // eslint-disable-next-line i18next/no-literal-string -- HTML tag names, not copy
    const Wrapper = inline ? 'span' : 'div';

    return (
      <Wrapper
        className={`${markdownStyle['markdown-body']} ${
          inline ? markdownStyle['markdown-body-inline'] : ''
        }`.trim()}
        data-testid="markdown-body"
        style={fadeStyle}
      >
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
          {animatedText}
        </ReactMarkdown>
      </Wrapper>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.content === nextProps.content &&
      prevProps.isStreaming === nextProps.isStreaming &&
      prevProps.disableAnimation === nextProps.disableAnimation &&
      prevProps.inline === nextProps.inline
    );
  },
);

ChatMarkdownRenderer.displayName = 'ChatMarkdownRenderer';
